import React, { useMemo, useState, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import { FaTrash } from 'react-icons/fa';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useUserReviews, useUpdateReviewHomepageStatus, useDeleteUserReview } from '../../queries/userReviews';
import ActiveStatus from '../../components/Common/ActiveStatus';

const UserReviews = () => {
  const { data: reviewDataList, isPending: isReviewDatalistPending } = useUserReviews();
  const { mutateAsync: updateHomepageStatus } = useUpdateReviewHomepageStatus();
  const { mutateAsync: deleteReview } = useDeleteUserReview();
  const columnHelper = createColumnHelper();

  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    username: '',
    rating: '',
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    let filtered = reviewDataList;

    if (filters.fromDate) {
      filtered = filtered.filter((review) => new Date(review.createdAt) >= new Date(filters.fromDate));
    }

    if (filters.toDate) {
      filtered = filtered.filter((review) => new Date(review.createdAt) <= new Date(filters.toDate));
    }

    if (filters.username) {
      filtered = filtered.filter((review) =>
        review.userId.username.toLowerCase().includes(filters.username.toLowerCase())
      );
    }

    if (filters.rating) {
      filtered = filtered.filter((review) => review.rating === parseInt(filters.rating));
    }

    setFilteredData(filtered);
  };

  const handleHomepageToggle = async (reviewId, currentStatus) => {
    await updateHomepageStatus(
      { reviewId, showHomePage: !currentStatus },
      {
        onSuccess: () => {
          toast.success(`Review ${!currentStatus ? 'added to' : 'removed from'} homepage successfully`);
        },
        onError: (error) => {
          toast.error("Failed to update homepage status: " + (error.message || "Unknown error"));
        }
      }
    );
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this review?");
    if (!confirm) return;

    await deleteReview(id, {
      onSuccess: () => {
        toast.success("Review deleted successfully");
      },
      onError: (error) => {
        toast.error("Failed to delete review: " + (error.message || "Unknown error"));
      }
    });
  };

  // Create columns definition
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'srno',
      header: 'Sr No.',
      cell: info => info.row.index + 1,
    }),
    columnHelper.accessor('userId.username', {
      header: 'Username',
      cell: info => info.getValue() || 'N/A',
    }),
    columnHelper.accessor('rating', {
      header: 'Rating',
      align: 'center',
      cell: info => (
        <div className='text-center flex items-center justify-center gap-1'>
          <span className='text-yellow-500'>⭐</span>
          <span>{info.getValue()}/5</span>
        </div>
      ),
    }),
    columnHelper.accessor('review', {
      header: 'Review',
      cell: info => (
        <div className='max-w-xs'>
          <p className='truncate' title={info.getValue()}>
            {info.getValue() || 'N/A'}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
      align: 'center',
      cell: info => (
        <div className='text-center'>
          {new Date(info.getValue()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
          <br />
          <small className='text-secondary'>
            {new Date(info.getValue()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </small>
        </div>
      ),
    }),
    columnHelper.accessor('updatedAt', {
      header: 'Updated At',
      align: 'center',
      cell: info => (
        <div className='text-center'>
          {new Date(info.getValue()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
          <br />
          <small className='text-secondary'>
            {new Date(info.getValue()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </small>
        </div>
      ),
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      align: 'center',
      enableColumnFilter: false,
      cell: info => {
        const review = info.row.original;
        return (
          <div className='flex items-center justify-center gap-2 flex-wrap'>
            <div className='min-w-fit'>
              <ActiveStatus
                active={review.showHomePage}
                setActive={() => handleHomepageToggle(review._id, review.showHomePage)}
                label=""
                innerActiveText="Show"
                innerInactiveText="Hide"
              />
            </div>
            <button
              className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
              onClick={() => handleDelete(review._id)}
              title="Delete Review"
            >
              <FaTrash className="fill-chipRed" />
            </button>
          </div>
        )
      },
    }),
  ], [updateHomepageStatus, deleteReview]);

  useEffect(() => {
    if (reviewDataList) {
      setFilteredData(reviewDataList);
    }
  }, [reviewDataList]);

  if (isReviewDatalistPending) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
          <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
            <h1 className="self-start text-lg font-semibold">User Reviews</h1>
            <p>Loading...</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
      {/* Filters Section */}
      <form
        className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full"
        onSubmit={handleSearch}
      >
        <div className='flex items-center justify-between pr-5'>
          <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 w-5 aspect-square"
            />
            <span className="self-stretch">Filters</span>
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
              alt=""
              className="object-contain shrink-0 aspect-square w-[18px]"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 p-5">
          <div className='flex gap-3 w-full'>
            <div className="flex-col w-full">
              <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                From Date
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                To Date
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="username" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Username
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  name="username"
                  value={filters.username}
                  onChange={handleFilterChange}
                  placeholder="Enter Username"
                  className='outline-none focus:ring-0 w-full'
                />
              </div>
            </div>
            <div className="flex-col w-full">
              <label htmlFor="rating" className="gap-1 self-stretch w-full text-gray-900 text-base">
                Rating
              </label>
              <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  name="rating"
                  value={filters.rating}
                  onChange={handleFilterChange}
                  className='outline-none focus:ring-0 w-full bg-transparent'
                >
                  <option value="">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className='flex gap-3 items-center justify-end p-5'>
          <ActionButton
            variant={'reset'}
            text={'Reset'}
            handleSubmit={() => {
              setFilters({
                fromDate: '',
                toDate: '',
                username: '',
                rating: '',
              });
              setFilteredData(reviewDataList);
            }}
          />
          <ActionButton
            variant={'submit'}
            text={'Search'}
            handleSubmit={() => { }}
          />
        </div>
      </form>

      {/* Reviews List Section */}
      <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
        <div className="flex flex-col pr-px w-full max-md:max-w-full">
          <h1 className="self-start text-lg font-bold pl-6 pb-4">User Reviews</h1>
          <div className='px-6 text-base'>
            <TanStackTable
              data={filteredData}
              columns={columns}
              enablePagination={true}
            />
          </div>
        </div>
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
      />
    </main>
  );
};

export default UserReviews;